/**
 * @fileoverview Local-first organization service for system-web.
 * @module system-web/services/organizationService
 *
 * Responsibilities: - Read/write organization records in Dexie (source of truth for UI)
 *                   - Push organization mutations to the server via apiClient wrappers
 * Dependencies:     - @firesquirrel/local-db, systemAuditLogService, apiClient (server sync only)
 * Architecture:     - Local-first. UI reads from Dexie; pages apply local changes via
 *                     upsertOrganization/deleteOrganizationLocal and then call the
 *                     *OnServer helpers to reconcile with the authoritative API.
 * Notes:            - Server calls live here (not in pages) per the data-access rule.
 */
import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { writeAuditLog } from './systemAuditLogService';
import { api } from '../apiClient';

export async function listOrganizations(): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.organizations.toArray();
  return docs.filter((d: any) => !d._deleted);
}

export async function getOrganization(id: string): Promise<any | null> {
  const db = await getSystemDb();
  const doc = await db.organizations.get(id);
  return doc ?? null;
}

export async function upsertOrganization(data: any): Promise<void> {
  const db = await getSystemDb();
  const isNew = !(await db.organizations.get(data._id));
  await db.organizations.put({ ...data, updatedAt: new Date().toISOString() });
  wakeLocalWrite('organizations');
  writeAuditLog({
    collection: 'organizations',
    documentId: data._id,
    action: isNew ? 'create' : 'update',
    summary: isNew ? `Created organization "${data.name}"` : `Updated organization "${data.name}"`,
  }).catch((err) => {
    console.warn('[organizationService] Failed to write audit log for organization upsert:', err);
  });
}

export async function deleteOrganizationLocal(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.organizations.get(id);
  if (!existing) return;
  const now = new Date().toISOString();
  await db.organizations.put({ ...(existing as any), _deleted: true, deletedAt: now, updatedAt: now });
  wakeLocalWrite('organizations');
}

/**
 * @process createOrganizationOnServer
 * Purpose: Push a locally-created organization to the server after the caller
 *          has already written it via upsertOrganization.
 * Steps:    1. Call api.organizations.create on the server.
 * Inputs:   organization payload.
 * Outputs:  server response (server-assigned document).
 * Failure Handling: Throws on server failure — callers attach .catch and warn.
 * Idempotency: NOT REQUIRED — server assigns the canonical id.
 */
export async function createOrganizationOnServer(data: any): Promise<any> {
  return api.organizations.create(data);
}

/**
 * @process updateOrganizationOnServer
 * Purpose: Push an organization update to the server after the caller has
 *          already applied it locally via upsertOrganization.
 * Steps:    1. Call api.organizations.update on the server.
 * Inputs:   organization id, full payload.
 * Outputs:  server response.
 * Failure Handling: Throws on server failure — callers attach .catch and warn.
 * Idempotency: REQUIRED — PUT with the same payload is safe to repeat.
 */
export async function updateOrganizationOnServer(id: string, data: any): Promise<any> {
  return api.organizations.update(id, data);
}

/**
 * @process updateOrganizationStatusOnServer
 * Purpose: Push an organization status change to the server after the caller
 *          has already applied it locally via upsertOrganization.
 * Steps:    1. Call api.organizations.updateStatus on the server.
 * Inputs:   organization id, status.
 * Outputs:  server response.
 * Failure Handling: Throws on server failure — callers attach .catch and warn.
 * Idempotency: REQUIRED — PATCHing the same status is safe to repeat.
 */
export async function updateOrganizationStatusOnServer(id: string, status: string): Promise<any> {
  return api.organizations.updateStatus(id, status);
}
